import React from "react"
import numeral from "numeral"
import {ConfirmationModal} from "../ConfirmationModal/ConfirmationModal"
import {Payment} from "../../resources/payment"
import {
	buildRecreatePaymentPayload,
	getAccountLimitsUrl,
	isPaymentLimitExceededReason,
} from "../../utilities/paymentPrefill"

export function RecreatePaymentModal({
	close,
	payment,
	onSubmit,
	navigate,
}: {
	close: () => void
	payment: Payment
	// The modal is closed (and unmounted) immediately on submit by ConfirmationModal, so the
	// create call and its result (toast + navigate) are owned by the caller instead of here.
	onSubmit: (recreatedPaymentPayload: Record<string, unknown>) => void
	navigate: (path: string) => void
}) {
	const recreate = buildRecreatePaymentPayload(payment, payment.id)
	const accountId = payment.relationships.account.data.id
	const limitExceeded = isPaymentLimitExceededReason(payment.attributes.reason)

	if (!recreate.ok) {
		return (
			<ConfirmationModal
				close={close}
				onSubmit={close}
				title="Create new payment"
				okButtonText="Close"
				okButtonClassname="button is-white"
				cancelButtonClassname="is-hidden"
			>
				<p>{recreate.message}</p>
			</ConfirmationModal>
		)
	}

	return (
		<ConfirmationModal
			close={close}
			onSubmit={() => onSubmit(recreate.payload)}
			title="Create new payment"
			okButtonText="Create payment"
			okButtonClassname="button is-success"
			cancelButtonClassname="button is-white"
		>
			<p>
				This creates a <strong>new</strong> payment with the same amount, account, and counterparty details as payment{" "}
				<strong>{payment.id}</strong>. Limit overrides apply only to newly submitted payments.
			</p>
			<p className="mt-3">
				Amount: <strong>{numeral(payment.attributes.amount / 100).format("$0,0.00")}</strong>
			</p>
			{limitExceeded && (
				<p className="mt-3">
					Rejected for <strong>{payment.attributes.reason}</strong>. Confirm the account limit override is active before
					submitting, then review limits on the{" "}
					<a
						href={getAccountLimitsUrl(accountId)}
						onClick={(e) => {
							e.preventDefault()
							close()
							navigate(getAccountLimitsUrl(accountId))
						}}
					>
						account limits page
					</a>
					.
				</p>
			)}
		</ConfirmationModal>
	)
}
